import * as React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WebIcon from "@mui/icons-material/Web";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import LaptopIcon from "@mui/icons-material/Laptop";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

/** The area of the app a help topic relates to */
export type HelpCategory = "website" | "headset" | "workstation" | "mobile";

export const helpCategoryConfig: Record<
  HelpCategory,
  { label: string; icon: React.ElementType }
> = {
  website: { label: "Website", icon: WebIcon },
  headset: { label: "Headset", icon: HeadsetMicIcon },
  workstation: { label: "Workstation", icon: LaptopIcon },
  mobile: { label: "Mobile", icon: PhoneIphoneIcon },
};

/**
 * Props for the StyledAccordion component
 */
interface StyledAccordionProps {
  /** The title text to display in the accordion header */
  title: string;
  /** Which areas of the app this topic relates to, shown as icons in the header */
  categories: HelpCategory[];
}

interface AccordionGroupContextValue {
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
}

const AccordionGroupContext = React.createContext<AccordionGroupContextValue | null>(null);

/** Wraps a set of StyledAccordions so that expanding one collapses the others */
export const AccordionGroup: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  return (
    <AccordionGroupContext.Provider value={{ expandedId, setExpandedId }}>
      {children}
    </AccordionGroupContext.Provider>
  );
};

/**
 * A styled accordion component that provides a consistent layout for collapsible content.
 *
 * Features:
 * - Material-UI accordion with expand/collapse functionality
 * - Typography variant h4 for the title
 * - Divider between header and content
 * - Stack layout with consistent spacing for children
 *
 * @param props - The component props
 * @param props.title - The title to display in the accordion header
 * @param props.categories - The areas of the app this topic relates to
 * @param props.children - The content to display in the accordion body
 * @returns A styled accordion component
 */
const StyledAccordion: React.FC<
  React.PropsWithChildren<StyledAccordionProps>
> = ({ title, categories, children }) => {
  // Generate unique IDs for accessibility
  const panelId = React.useMemo(
    () => `accordion-panel-${title.toLowerCase().replace(/\s+/g, "-")}`,
    [title]
  );
  const headerId = `${panelId}-header`;

  const group = React.useContext(AccordionGroupContext);
  const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(false);

  const expanded = group ? group.expandedId === panelId : uncontrolledExpanded;
  const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (group) {
      group.setExpandedId(isExpanded ? panelId : null);
    } else {
      setUncontrolledExpanded(isExpanded);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange}>
      {/* Accordion header with title and expand icon */}
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${panelId}-content`}
        id={headerId}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pr: 1 }}
        >
          <Typography variant="h4">{title}</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            {categories.map((category) => {
              const { label: categoryLabel, icon: CategoryIcon } = helpCategoryConfig[category];

              return (
                <CategoryIcon
                  key={category}
                  titleAccess={categoryLabel}
                  sx={{ color: "text.secondary", fontSize: "1.4rem" }}
                />
              );
            })}
          </Stack>
        </Stack>
      </AccordionSummary>

      {/* Visual separator between header and content */}
      <Divider />

      {/* Accordion content area with consistent spacing */}
      <AccordionDetails>
        <Stack spacing={2}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default StyledAccordion;
